import { useContext, useMemo } from "react";
import { ContextApp } from "@config/Context/contextApp";
import { useSettingsStore } from "@context/useSettingsStore";
import { Bundle, Link, Group } from "@models/ISchema";

/**
 * Single source of truth for the bundle-vacancies edit permissions.
 *
 * Every "can I enter edit mode?" and "which field is editable?" flag is derived
 * here from the same inputs (the user abilities in `ContextApp` plus the
 * `show_retakers_columns` setting), so the vacancies tab, the instructor tab and
 * the groups table can no longer drift apart. Components only consume the flags.
 *
 * `dataBundle` is optional: the field-level flags (`canEditVacancies`,
 * `canEditRetakers`, `canSelectReferent`, `showRetakersColumns`) don't depend on
 * it, so a call site that only renders fields (e.g. the groups table) can omit
 * it and ignore the entry gates.
 */
export const useBundleEditAbilities = (dataBundle?: Bundle) => {
  const context = useContext(ContextApp);
  const showRetakersColumns = useSettingsStore(state => state.showRetakersColumns);

  const canUpdate = !!context?.user?.permissions?.update;
  const canEditGroups = !!context?.user?.abilities?.can_edit_groups_in_bundle_view;
  const canEditRetakers = !!context?.user?.abilities?.can_edit_retaker_capacities;

  const canEditSomeGroup = useMemo(
    () =>
      dataBundle
        ? dataBundle.links.some((link: Link) =>
            link.groups.some((group: Group) => group.course.edhCanEdit),
          )
        : false,
    [dataBundle],
  );

  // Entry gates differ per tab: the vacancies tab also opens for a retaker-only
  // user (when the retaker column is visible); the instructor tab never does.
  const canEnterVacanciesEdit =
    canUpdate && (canEditGroups || (canEditRetakers && showRetakersColumns)) && canEditSomeGroup;

  const canEnterInstructorEdit = canUpdate && canEditGroups && canEditSomeGroup;

  // Field-level gates. `canEditVacancies` governs the regular group fields
  // (vacancies, active, visibility); `canSelectReferent` governs the referent
  // radio. Both derive from `can_edit_groups_in_bundle_view` today, but are
  // exposed separately so a future divergence only touches this hook.
  const canEditVacancies = canUpdate && canEditGroups;
  const canSelectReferent = canUpdate && canEditGroups;

  return {
    canEnterVacanciesEdit,
    canEnterInstructorEdit,
    canEditVacancies,
    canEditRetakers,
    canSelectReferent,
    showRetakersColumns,
  };
};
